import { Box, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router-dom';

import { detailsHref, isDetailsPathname, projectBasePath } from './projectAreas';

import { useProject } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { useProjectManagerName } from '@/components/projectDetails/state';
import { PROJECT_SETTINGS_FLAG, useFeatureFlag } from '@/featureFlags';

/**
 * The project header's second line, on every tab (docs/briefs/
 * project-details-v2/brief.md §3 and decision 1): `Customer · PM · Project
 * details ›`. It reads its own data — the route's project for the customer,
 * Project details' store for the project manager — so no page has to pass it
 * anything, and Planning and Financials are untouched.
 *
 * Label size throughout (Mette, 2 Nov): it is metadata under the title.
 *
 * The line itself is not flagged (Mette, 30 Oct). The Project details link is,
 * because the page it opens is: with the flag off the line is `Customer · PM`.
 */
export const ProjectHeaderMeta = () => {
  const { t } = useTranslation('pmShell');
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();
  const { data } = useProject(projectId);
  const projectManager = useProjectManagerName(projectId ?? '');
  const detailsEnabled = useFeatureFlag(PROJECT_SETTINGS_FLAG);
  const base = projectBasePath({ projectId });

  const customer = data?.document.customer?.snapshot.name;
  const facts = [
    customer ? { key: 'customer', text: customer } : null,
    projectManager ? { key: 'project-manager', text: projectManager } : null,
  ].filter((fact): fact is { key: string; text: string } => fact !== null);
  const showLink = detailsEnabled && base !== null;
  const current = isDetailsPathname(pathname);

  if (facts.length === 0 && !showLink) return null;

  const dot = (
    <Box component="span" aria-hidden sx={{ color: 'var(--color-fog-80)' }}>
      ·
    </Box>
  );

  return (
    <Stack
      direction="row"
      data-automation-id="project-header-meta"
      sx={{ alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minHeight: 20 }}
    >
      {facts.map((fact, index) => (
        <Fragment key={fact.key}>
          {index > 0 ? dot : null}
          <Typography
            variant="label"
            component="span"
            sx={{ color: 'var(--color-fog-80)' }}
            data-automation-id={`project-header-${fact.key}`}
          >
            {fact.text}
          </Typography>
        </Fragment>
      ))}
      {showLink ? (
        <>
          {facts.length > 0 ? dot : null}
          <Box
            component={NavLink}
            to={detailsHref(base)}
            aria-current={current ? 'page' : undefined}
            data-automation-id="nav-project-details"
            data-selected={current}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              height: 20,
              pl: 1,
              pr: 0.25,
              borderRadius: 'var(--border-radius-pill)',
              color: 'var(--color-navy-100)',
              fontSize: 'var(--font-size-label)',
              fontWeight: current ? 500 : 400,
              bgcolor: current ? 'var(--color-fog-20)' : 'transparent',
              textDecoration: 'none',
              '&:hover': { bgcolor: 'var(--color-fog-20)' },
              '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '2px' },
            }}
          >
            {t('header.ProjectDetails')}
            <MaterialSymbol name="chevron_right" size={14} />
          </Box>
        </>
      ) : null}
    </Stack>
  );
};
